import Phaser from 'phaser'
import './styles.css'
import { gameBus } from './bus/GameEventBus'
import { onLocaleChange, t } from './i18n'
import { createGameConfig } from './game/JKGame_config'
import { CHAPTERS, findStoryGraphErrors, SPEAKERS } from './story/JKGame_story'
import { PORTRAIT_SETS } from './game/JKGame_assets'
import * as JKGame_api from './services/JKGame_api'
import * as JKGame_backendLog from './services/JKGame_backendLog'
import * as JKGame_backendState from './services/JKGame_backendState'
import * as JKGame_economy from './services/JKGame_economy'
import { matchController } from './services/JKGame_MatchController'
import { mountAuthOverlay } from './ui/JKGame_authOverlay'

// Production auto-detect: probe the authoritative backend ASAP so the game uses the real economy
// (real gacha / persistence / leaderboards) when it's reachable, and falls back to the offline
// demo otherwise. Fire-and-forget — resolves within the boot window, before the player reaches
// Gacha/Menu; an explicit ?backend=0|1 (or localStorage) still overrides it.
void JKGame_api.probeBackend()

const game = new Phaser.Game(createGameConfig('JKGame_game'))

// Pre-game web UI: a landing / register / login DOM overlay covering the canvas until the player
// enters (or, if already signed in, it never shows). The game mounts underneath either way, so
// flag-off behaviour is unchanged — this is purely an entry gate outside the Phaser/sim layer.
mountAuthOverlay()

// Portrait-first: show a localized rotate overlay in landscape on touch devices.
// Never a silent break — desktop landscape just letterboxes via Scale.FIT.
const overlay = document.getElementById('JKGame_rotate')
// Localize the rotate overlay to the active locale (static HTML is the zh-TW
// default for first paint; this keeps it in sync with the player's choice).
function localizeRotateOverlay(): void {
  if (!overlay) return
  const title = overlay.querySelector('.rotate-title')
  const sub = overlay.querySelector('.rotate-sub')
  if (title) title.textContent = t('rotate.title')
  if (sub) sub.textContent = t('rotate.sub')
}
function updateOrientation(): void {
  if (!overlay) return
  const landscape = window.innerWidth > window.innerHeight
  const touch = window.matchMedia('(pointer: coarse)').matches
  const blocking = landscape && touch
  if (blocking) localizeRotateOverlay()
  overlay.hidden = !blocking
  // Freeze the Phaser loop while the rotate overlay covers the game: otherwise a match's timed
  // sim (bot wander / idle ticks) keeps advancing UNSEEN behind the overlay and the player
  // loses agency to a mere device rotation ("landscape must not silently break gameplay").
  // Deterministic & flag-safe: sleep/wake only gates wall-clock — the sim resumes on the same
  // RNG sequence with identical outcomes. Triggers ONLY in the already-degraded mobile-landscape
  // state; desktop and portrait play never sleep the loop. wake(true) avoids a resume time-jump.
  if (game.isBooted) {
    if (blocking) game.loop.sleep()
    else if (!game.loop.running) game.loop.wake(true)
  }
}
window.addEventListener('resize', updateOrientation)
window.addEventListener('orientationchange', updateOrientation)
onLocaleChange(localizeRotateOverlay)
localizeRotateOverlay()
updateOrientation()

if (import.meta.env.DEV) {
  Object.assign(window as object, { JKGame: game, JKGame_bus: gameBus, JKGame_ctrl: matchController, JKGame_api, JKGame_backendLog, JKGame_backendState, JKGame_economy })
  // Dev guard: every scene a button navigates to must be registered, else a tap
  // is a silent dead end (caught ResultScene→'Match' once). Warn early if drift.
  const NAV_TARGETS = ['Boot', 'Menu', 'Settings', 'Story', 'Gacha', 'Shop', 'Collection', 'Progress', 'Explore', 'Combat', 'Result']
  game.events.once(Phaser.Core.Events.READY, () => {
    const missing = NAV_TARGETS.filter((k) => !game.scene.getScene(k))
    if (missing.length) console.warn('[JKGame] navigation targets not registered:', missing)
  })
  // Dev guard: every story transition (chapter start / scene next / choice goto) must resolve
  // to a scene in that chapter. A dangling target is NOT a crash — gotoScene falls back to
  // endChapter — so a typo'd 'goto' would SILENTLY truncate the chapter. Surface it early.
  // Shares findStoryGraphErrors with StoryScene's runtime guard (#229) so there's ONE source
  // of truth for graph validity (also catches a scene with both `choices` and `next`).
  for (const ch of CHAPTERS) {
    const errs = findStoryGraphErrors(ch)
    if (errs.length) console.warn(`[JKGame] chapter '${ch.id}' story graph errors:`, errs)
  }
  // Dev guard: a speaker's portrait `set` is a plain string (unlike `emotion`, which is the
  // typed Emotion union TS validates), so a typo like 'fem_99' compiles fine but makes
  // StoryScene call porKey() for a texture that was never loaded → a silently blank/wrong
  // portrait. Verify every speaker set resolves to a real portrait set before it ships.
  const sets = new Set(PORTRAIT_SETS)
  const badSets = Object.entries(SPEAKERS)
    .filter(([, s]) => s.set !== undefined && !sets.has(s.set))
    .map(([id, s]) => `${id}→'${s.set}'`)
  if (badSets.length) console.warn('[JKGame] speakers with an unknown portrait set:', badSets)
  // The above skips undefined sets (officer/voice legitimately have none), but StoryScene's
  // initial portrait (porKey(SPEAKERS.you.set, ...)) UNCONDITIONALLY needs the protagonist's
  // set — a removed `you.set` would slip past both that `as string` cast and the filter above,
  // yielding 'por_undefined_*'. Assert the one set the scene hard-depends on actually resolves.
  if (SPEAKERS.you.set === undefined || !sets.has(SPEAKERS.you.set)) {
    console.warn("[JKGame] protagonist 'you' has no valid portrait set — StoryScene's initial portrait will be invalid:", SPEAKERS.you.set)
  }
}
